import {h, spec, list} from '../../h'
import {createEvent, Store, is, combine} from '@effector/effector'

import {
  stores,
  events,
  effects,
  samples,
  ons,
  watches,
  forwards,
  selectUnits,
  primeUnits,
  RuntimeOn,
  RuntimeEvent,
  RuntimeEffect,
  RuntimeStore,
  RuntimePrimeUnit,
  RuntimeSample
} from '../../runtime/units'
import {MD, BLACK} from '../../env'
import {generatePath, M, L, Z, Q, point} from '../../path'
import {remap} from '../../storeField'

import {TreeNode, draw} from '../../treeDraw'

function applyUnitFN<T, R>(value: Store<T> | T, fn: (_: T) => R): Store<R> | R {
  return is.unit(value) ? value.map(fn) : fn(value)
}

const STROKE_BASE = MD / 5
const FIGURE_SIZE = MD * 1.5
const TEXT_PADDING_LEFT = MD * 1.25
const UNIT_GROUP_OFFSET_TOP = MD * 4
const OFFSET_Y = FIGURE_SIZE / 2
const ROW_SIZE = MD * 3
const DIAG_PADDING_LEFT = MD * 12

function stroke(strokeQ: number | Store<number> = 1) {
  spec({
    attr: {
      fill: 'transparent',
      stroke: BLACK,
      'stroke-width': applyUnitFN(strokeQ, q => STROKE_BASE * q)
    }
  })
}
function getUnitInfo(
  label: Store<string>,
  primeUnits: Store<RuntimePrimeUnit[]>
) {
  const unit = combine(label, primeUnits, (label, units) =>
    units.find(e => e.node.meta.name === label || e.name === label)
  )
  const unitType = remap(unit, 'unit')
  const isSquare = unitType.map(type => type === 'store')
  const isCircle = unitType.map(type => type !== 'store')
  return {
    unit,
    type: unitType,
    isSquare,
    isCircle
  }
}

function UnitSymbol({
  isCircle,
  isSquare
}: {
  isCircle: Store<boolean>
  isSquare: Store<boolean>
}) {
  EventSymbol({isCircle})
  StoreSymbol({isSquare})
}

function EventSymbol({isCircle}: {isCircle: Store<boolean>}) {
  h('circle', () => {
    stroke()
    spec({
      attr: {
        r: FIGURE_SIZE / 2
      },
      visible: isCircle
    })
  })
}
function StoreSymbol({isSquare}: {isSquare: Store<boolean>}) {
  h('rect', () => {
    stroke()
    spec({
      attr: {
        x: -FIGURE_SIZE / 2,
        y: -FIGURE_SIZE / 2,
        width: FIGURE_SIZE,
        height: FIGURE_SIZE
      },
      visible: isSquare
    })
  })
}
function EdgeLine(content: () => void) {
  h('polyline', () => {
    stroke(0.3)
    spec({
      attr: {
        'stroke-linejoin': 'round',
        'marker-end': 'url(#arrow)'
      }
    })
    content()
  })
}

function ArrowDef() {
  h('defs', () => {
    h('marker', () => {
      spec({
        attr: {
          id: 'arrow',
          viewBox: '0 0 10 10',
          refX: 7,
          refY: 5,
          markerUnits: 'userSpaceOnUse',
          markerWidth: 5,
          markerHeight: 5,
          orient: 'auto'
        }
      })
      h('path', {
        attr: {
          fill: BLACK,
          d: generatePath(() => {
            M(0, 0)
            L(10, 5)
            L(0, 10)
            Z()
          })
        }
      })
    })
  })
}

function SamplesDiargam({
  samples,
  ons,
  primeUnits
}: {
  samples: Store<RuntimeSample[]>
  ons: Store<RuntimeOn[]>
  primeUnits: Store<RuntimePrimeUnit[]>
}) {
  const groupByTarget = combine(
    primeUnits,
    samples,
    ons,
    (units, samples, ons) => {
      const resultMap = new Map<
        RuntimePrimeUnit,
        {
          samples: RuntimeSample[]
          ons: RuntimeOn[]
        }
      >()
      const result = [] as Array<{
        target: RuntimeEvent | RuntimeEffect | RuntimeStore
        samples: RuntimeSample[]
        ons: RuntimeOn[]
        firstRow: number
        groupIndex: number
      }>
      function upsertSubset(primeUnit) {
        let subset: {
          samples: RuntimeSample[]
          ons: RuntimeOn[]
        } = resultMap.get(primeUnit)
        if (!subset) {
          subset = {
            samples: [],
            ons: []
          }
          resultMap.set(primeUnit, subset)
        }
        return subset
      }
      for (const u of samples) {
        const target = units.find(
          unit => unit.node.meta.name === u.target || unit.name === u.target
        )
        // console.log('target', target, 'unit', u)
        if (!target) {
          console.log('no target', units, u)
        }
        upsertSubset(target).samples.push(u)
      }
      for (const on of ons) {
        const source = units.find(unit => unit.name === on.source)
        if (!source) {
          console.log('no source', units, on)
        }
        upsertSubset(source).ons.push(on)
      }
      let firstRow = 0
      let groupIndex = 0
      //@ts-ignore
      for (const [target, {samples, ons}] of resultMap) {
        result.push({
          target,
          samples,
          ons,
          firstRow,
          groupIndex
        })
        firstRow += samples.length * 2
        firstRow += ons.length
        groupIndex += 1
      }
      return result
    }
  )
  h('g', () => {
    spec({
      transform: {
        translate: {
          x: DIAG_PADDING_LEFT,
          y: UNIT_GROUP_OFFSET_TOP
        }
      }
    })
    list(groupByTarget, ({store}) => {
      const [target, samples, ons] = remap(store, [
        'target',
        'samples',
        'ons'
      ] as const)
      const onsAmount = ons.map(list => list.length)
      const samplesAmount = samples.map(list => list.length)
      const totalAmount = combine(
        onsAmount,
        samplesAmount,
        (ons, samples) => ons + samples
      )
      const targetOffsetY = store.map(
        ({ons, samples}) =>
          (samples.length * 2 + ons.length) * ROW_SIZE - OFFSET_Y
      )

      const targetWidth = totalAmount.map(size => size * FIGURE_SIZE)
      const textOffset = totalAmount.map(
        size => (size - 1) * FIGURE_SIZE + TEXT_PADDING_LEFT
      )
      store.watch(t => {
        console.log('sample group', t)
      })
      const targetLabel = remap(target, 'name')
      const groupOffsetY = store.map(
        ({firstRow, groupIndex}) => (firstRow + groupIndex * 2) * ROW_SIZE
      )

      h('g', () => {
        spec({
          transform: {
            translate: {
              x: MD * 12,
              y: groupOffsetY
            }
          }
        })
        ons: h('g', () => {
          clock: h('g', () => {
            list(ons, ({store, index}) => {
              const clock = remap(store, 'clock')
              const clockInfo = getUnitInfo(clock, primeUnits)
              h('g', () => {
                spec({
                  transform: {
                    translate: {
                      x: -MD * 5,
                      y: index * ROW_SIZE
                    }
                  }
                })
                UnitSymbol(clockInfo)
                h('text', {
                  text: clock,
                  attr: {
                    x: -STROKE_BASE * 2 - FIGURE_SIZE / 2,
                    y: 0,
                    'dominant-baseline': 'middle',
                    'text-anchor': 'end'
                  }
                })
              })
            })
          })
          ops: h('g', () => {
            list(ons, ({store, index}) => {
              h('g', () => {
                spec({
                  transform: {
                    translate: {
                      y: index * ROW_SIZE,
                      x: -FIGURE_SIZE
                    }
                  }
                })
                h('rect', () => {
                  stroke(0.5)
                  spec({
                    attr: {
                      x: -FIGURE_SIZE / 2,
                      y: -FIGURE_SIZE / 2,
                      width: FIGURE_SIZE,
                      height: FIGURE_SIZE
                    }
                  })
                })

                h('text', {
                  text: store.map(e => (e.isReset ? 'reset' : 'on')),
                  attr: {
                    'font-size': MD * 0.75,
                    'font-style': 'italic',
                    'text-anchor': 'middle',
                    'dominant-baseline': 'middle'
                  }
                })
              })
            })
          })
          lines: h('g', () => {
            spec({
              transform: {
                translate: {
                  x: -FIGURE_SIZE * 2.5
                }
              }
            })
            list(ons, ({index}) => {
              h('g', () => {
                spec({
                  transform: {
                    translate: {
                      y: index * ROW_SIZE
                    }
                  }
                })
                EdgeLine(() => {
                  spec({
                    attr: {
                      points: generatePath(() => {
                        point(0, 0)
                        point(FIGURE_SIZE - STROKE_BASE * 2, 0)
                      })
                    }
                  })
                })
                EdgeLine(() => {
                  const targetRow = combine(
                    onsAmount,
                    samplesAmount,
                    (ons, samples) => ons + samples * 2 - index
                  )
                  const targetCol = combine(
                    onsAmount,
                    samplesAmount,
                    (ons, samples) => ons + samples - index
                  )

                  const points = combine(
                    targetRow,
                    targetCol,
                    (targetRow, targetCol) =>
                      generatePath(() => {
                        const BASE_OFFSET_X = FIGURE_SIZE * 2
                        const lineLength = targetRow * FIGURE_SIZE
                        const xOffset =
                          BASE_OFFSET_X +
                          targetCol * FIGURE_SIZE +
                          FIGURE_SIZE * 0.5
                        point(BASE_OFFSET_X + STROKE_BASE * 2, 0)
                        point(xOffset, 0)
                        point(xOffset, lineLength)
                        if (targetRow > 1) {
                          point(
                            xOffset,
                            lineLength +
                              FIGURE_SIZE * (targetRow - 1) +
                              FIGURE_SIZE * 0.5 -
                              STROKE_BASE * 2
                          )
                        } else {
                          point(
                            xOffset,
                            lineLength + FIGURE_SIZE * 0.5 - STROKE_BASE * 2
                          )
                        }
                      })
                  )
                  spec({
                    attr: {
                      points
                    }
                  })
                })
              })
            })
          })
        })
        samples: h('g', () => {
          spec({
            transform: {
              translate: {
                y: onsAmount.map(amount => amount * ROW_SIZE)
              }
            }
          })
          h('g', () => {
            spec({
              transform: {
                translate: {
                  x: -MD * 5
                }
              }
            })
            h('g', () => {
              list(samples, ({store, index}) => {
                const source = remap(store, 'source')
                const sourceInfo = getUnitInfo(source, primeUnits)
                h('g', () => {
                  spec({
                    transform: {
                      translate: {
                        y: index * 2 * ROW_SIZE
                      }
                    }
                  })
                  UnitSymbol(sourceInfo)
                  h('text', {
                    text: source,
                    attr: {
                      x: -STROKE_BASE * 2 - FIGURE_SIZE / 2,
                      y: 0,
                      'dominant-baseline': 'middle',
                      'text-anchor': 'end'
                    }
                  })
                })
              })
            })

            h('g', () => {
              list(samples, ({store, index}) => {
                const clock = remap(store, 'clock')
                const clockInfo = getUnitInfo(clock, primeUnits)
                h('g', () => {
                  spec({
                    transform: {
                      translate: {
                        y: (index * 2 + 1) * ROW_SIZE
                      }
                    }
                  })
                  UnitSymbol(clockInfo)
                  h('text', {
                    text: clock,
                    attr: {
                      x: -STROKE_BASE * 2 - FIGURE_SIZE / 2,
                      y: 0,
                      'dominant-baseline': 'middle',
                      'text-anchor': 'end'
                    }
                  })
                })
              })
            })
          })
          lines: h('g', () => {
            spec({
              transform: {
                translate: {
                  x: -FIGURE_SIZE * 2.5
                }
              }
            })
            list(samples, ({index}) => {
              h('g', () => {
                spec({
                  transform: {
                    translate: {
                      y: index * 2 * ROW_SIZE
                    }
                  }
                })
                EdgeLine(() => {
                  spec({
                    attr: {
                      'stroke-dasharray': '4 1',
                      points: generatePath(() => {
                        point(0, 0)
                        point(FIGURE_SIZE * 1.5, 0)
                        point(
                          FIGURE_SIZE * 1.5,
                          FIGURE_SIZE * 1.5 - STROKE_BASE * 2
                        )
                      })
                    }
                  })
                })
                EdgeLine(() => {
                  spec({
                    attr: {
                      points: generatePath(() => {
                        point(0, ROW_SIZE)
                        point(FIGURE_SIZE - STROKE_BASE * 2, ROW_SIZE)
                      })
                    }
                  })
                })
                EdgeLine(() => {
                  const targetIndex = samplesAmount.map(size => size - index)

                  const points = targetIndex.map(targetIndex =>
                    generatePath(() => {
                      const BASE_OFFSET_X = FIGURE_SIZE * 2
                      const lineLength = targetIndex * FIGURE_SIZE
                      const xOffset =
                        BASE_OFFSET_X + lineLength + FIGURE_SIZE * 0.5
                      const FIGURE_OFFSET = FIGURE_SIZE * 0.5 - STROKE_BASE * 2
                      point(BASE_OFFSET_X + STROKE_BASE * 2, ROW_SIZE)
                      point(xOffset, ROW_SIZE)
                      point(
                        xOffset,
                        lineLength * 2 +
                          FIGURE_SIZE * 2 * (targetIndex - 0.5) +
                          FIGURE_OFFSET
                      )
                    })
                  )
                  spec({
                    attr: {
                      points
                    }
                  })
                })
              })
            })
          })
          ops: h('g', () => {
            const lineOffset =
              (FIGURE_SIZE / 2) * Math.sqrt((Math.sqrt(2) - 1) ** 2 / 2)
            list(samples, ({store, index}) => {
              h('g', () => {
                spec({
                  transform: {
                    translate: {
                      y: (index * 2 + 1) * ROW_SIZE,
                      x: -FIGURE_SIZE
                    }
                  }
                })
                h('circle', () => {
                  stroke(0.5)
                  spec({
                    attr: {
                      r: FIGURE_SIZE / 2
                    }
                  })
                })
                h('path', () => {
                  // stroke(0.5)
                  spec({
                    attr: {
                      fill: BLACK,
                      stroke: 'none',
                      d: generatePath({round: false}, () => {
                        M(
                          -FIGURE_SIZE / 2 + lineOffset,
                          -FIGURE_SIZE / 2 + lineOffset
                        )
                        Q({
                          //TODO wtf
                          x1: (-FIGURE_SIZE / 2) * (4 / 3),
                          y1: 0,
                          x: -FIGURE_SIZE / 2 + lineOffset,
                          y: FIGURE_SIZE / 2 - lineOffset
                        })
                        L(0, 0)
                        Z()
                      })
                    }
                  })
                })
                h('line', () => {
                  stroke(0.5)
                  spec({
                    attr: {
                      x1: -FIGURE_SIZE / 2 + lineOffset,
                      y1: -FIGURE_SIZE / 2 + lineOffset,
                      x2: +FIGURE_SIZE / 2 - lineOffset,
                      y2: +FIGURE_SIZE / 2 - lineOffset
                    }
                  })
                })
                h('line', () => {
                  stroke(0.5)
                  spec({
                    attr: {
                      x1: -FIGURE_SIZE / 2 + lineOffset,
                      y1: +FIGURE_SIZE / 2 - lineOffset,
                      x2: +FIGURE_SIZE / 2 - lineOffset,
                      y2: -FIGURE_SIZE / 2 + lineOffset
                    }
                  })
                })
                // h('rect', () => {
                //   stroke(0.5)
                //   spec({
                //     attr: {
                //       x: -FIGURE_SIZE / 2,
                //       y: -FIGURE_SIZE / 2,
                //       width: FIGURE_SIZE,
                //       height: FIGURE_SIZE
                //     }
                //   })
                // })
              })
            })
          })
        })
        target: h('g', () => {
          spec({
            transform: {
              translate: {
                y: targetOffsetY,
                x: FIGURE_SIZE
              }
            }
          })
          h('rect', () => {
            stroke()
            spec({
              attr: {
                x: -FIGURE_SIZE / 2,
                width: targetWidth,
                height: FIGURE_SIZE
              }
            })
          })
          h('text', {
            text: targetLabel,
            attr: {
              x: textOffset,
              y: OFFSET_Y,
              'dominant-baseline': 'middle'
            }
          })
        })
      })
    })
  })
}

export function Diagrams({
  ons,
  samples,
  primeUnits
}: {
  ons: Store<RuntimeOn[]>
  samples: Store<RuntimeSample[]>
  primeUnits: Store<RuntimePrimeUnit[]>
}) {
  h('svg', () => {
    spec({
      attr: {
        width: 1400,
        height: combine(samples, ons, (samples, ons) => {
          return (
            (samples.length * 4 + ons.length * 3) * ROW_SIZE +
            UNIT_GROUP_OFFSET_TOP
          )
        })
      }
    })
    ArrowDef()
    SamplesDiargam({
      primeUnits,
      samples,
      ons
    })
  })
}

function TreeExample() {
  const a = new TreeNode('a', [
    new TreeNode('b', [
      new TreeNode('c', [
        new TreeNode('d'),
        new TreeNode('e'),
        new TreeNode('h'),
        new TreeNode('i')
      ]),
      new TreeNode('f', [new TreeNode('g')])
    ])
  ])

  h('g', () => {
    spec({
      transform: {
        translate: {
          x: 350,
          y: MD * 4
        }
      }
    })

    const TREE_PADDING = 10
    const PATH_PADDING = 2
    const STROKE_PADDING = 1
    const PATH_JOINT_OFFSET =
      (TREE_PADDING * 2 - PATH_PADDING * 2 - STROKE_PADDING) / 3
    draw(
      a,
      {width: 200, height: 200, direction: 'horizontal'},
      (node, {treeSize, levelSize, nodeSize, direction}) => {
        let x1
        let y1
        if (direction === 'horizontal') {
          x1 = (node.x + 0.5) * levelSize.x - TREE_PADDING * 2 + PATH_PADDING
          y1 = node.y * levelSize.y - TREE_PADDING
        } else {
          x1 = node.x * levelSize.x - TREE_PADDING
          y1 = (node.y + 0.5) * levelSize.y - TREE_PADDING * 2 + PATH_PADDING
        }
        node.children.forEach(({x, y}) => {
          let x2
          let y2
          if (direction === 'horizontal') {
            x2 =
              x * levelSize.x - nodeSize.x / 2 - PATH_PADDING - STROKE_PADDING
            y2 = y * levelSize.y - TREE_PADDING
          } else {
            x2 = x * levelSize.x - TREE_PADDING
            y2 =
              y * levelSize.y - nodeSize.y / 2 - PATH_PADDING - STROKE_PADDING
          }
          h('path', {
            attr: {
              d:
                direction === 'horizontal'
                  ? `M${x1 | 0} ${y1 | 0} h${PATH_JOINT_OFFSET | 0} V${y2 |
                      0} H${x2 | 0}`
                  : `M${x1 | 0} ${y1 | 0} v${PATH_JOINT_OFFSET | 0} H${x2 |
                      0} V${y2 | 0}`,
              fill: 'transparent',
              stroke: BLACK,
              'stroke-width': 0.75,
              'stroke-linejoin': 'round',
              'marker-end': 'url(#arrow)'
            }
          })
        })
      }
    )
    draw(
      a,
      {width: 200, height: 200, direction: 'horizontal'},
      (node, {treeSize, levelSize, nodeSize}) => {
        const x1 = node.x * levelSize.x
        const y1 = node.y * levelSize.y
        // h('rect', {
        //   attr: {
        //     fill: 'transparent',
        //     stroke: 'black',
        //     x: x1 - nodeSize.x / 2,
        //     y: y1 - nodeSize.y / 2,
        //     width: nodeSize.x - TREE_PADDING * 2,
        //     height: nodeSize.y - TREE_PADDING * 2
        //   }
        // })
        h('circle', {
          attr: {
            fill: 'transparent',
            stroke: BLACK,
            'stroke-width': 0.75,
            cx: (x1 - TREE_PADDING) | 0,
            cy: (y1 - TREE_PADDING) | 0,
            r: (nodeSize.x - TREE_PADDING * 2) / 2 / 1.25
          }
        })
        h('text', {
          attr: {
            x: (x1 - TREE_PADDING) | 0,
            y: (y1 - TREE_PADDING) | 0,
            'dominant-baseline': 'central',
            'text-anchor': 'middle'
          },
          text: node.value
        })
      }
    )
  })
}
